// Text for the "Connect" page (Cargo and containers). Pure functions, so tests can check the exact output.
import { DEFAULT_STS_URL } from "../config";

export interface SnippetInput {
  tenant: string;
  namespace: string;
  cargoBaseUrl: string;
  stsUrl: string;
  audience: string;
}

/** Cargo registry name: the tenant slug for `default`, else `<tenant>-<namespace>`. */
export function registryName(tenant: string, namespace: string): string {
  return namespace === "default" ? tenant : `${tenant}-${namespace}`;
}

/** Sparse index URL of a namespace (docs/design.md section 7). */
export function indexUrl(cargoBaseUrl: string, tenant: string, namespace: string): string {
  return `sparse+${cargoBaseUrl.replace(/\/+$/, "")}/t/${tenant}/${namespace}/index/`;
}

function providerArgs(i: SnippetInput): string {
  const args = ["cargo-credential-prego", "--tenant", i.tenant];
  if (i.stsUrl !== DEFAULT_STS_URL) args.push("--sts-url", i.stsUrl);
  if (i.audience !== "prego.k8skill.com") args.push("--audience", i.audience);
  return `[${args.map((a) => JSON.stringify(a)).join(", ")}]`;
}

/** `.cargo/config.toml` for a namespace. */
export function cargoConfigToml(i: SnippetInput): string {
  return [
    "# .cargo/config.toml",
    `[registries.${registryName(i.tenant, i.namespace)}]`,
    `index = "${indexUrl(i.cargoBaseUrl, i.tenant, i.namespace)}"`,
    `credential-provider = ${providerArgs(i)}`,
    "",
  ].join("\n");
}

/** Dependency line for `Cargo.toml`. */
export function cargoDependency(i: SnippetInput, crate = "my-crate", version = "0.1"): string {
  return [
    "# Cargo.toml",
    "[dependencies]",
    `${crate} = { version = "${version}", registry = "${registryName(i.tenant, i.namespace)}" }`,
    "",
  ].join("\n");
}

/** GitLab CI job that gets an ID token for Prego (docs/credential-provider.md section 3). */
export function gitlabCiYaml(i: SnippetInput): string {
  const reg = registryName(i.tenant, i.namespace);
  return [
    "# .gitlab-ci.yml",
    "build:",
    "  image: rust:latest",
    "  id_tokens:",
    "    PREGO_ID_TOKEN:",
    `      aud: ${i.audience}`,
    "  script:",
    "    - cargo install cargo-credential-prego --locked",
    "    - cargo build --locked",
    `    # To publish: cargo publish --registry ${reg}`,
    "",
  ].join("\n");
}

/** Input for the container snippets (ADR-0033 sections 2, 4 and 12). */
export interface OciSnippetInput {
  tenant: string;
  namespace: string;
  /** Host name only, for example oci.prego.k8skill.com. */
  ociHost: string;
  audience: string;
}

/** Example repository and tag in the snippets. */
export const EXAMPLE_REPO = "app";
export const EXAMPLE_TAG = "1.0.0";

/** Image name: `<host>/<tenant>/<namespace>/<repo>`. The namespace is always written, `default` too. */
export function imageName(i: OciSnippetInput, repo = EXAMPLE_REPO): string {
  return `${i.ociHost}/${i.tenant}/${i.namespace}/${repo}`;
}

/** docker login with an API key, then pull and push. The username is the tenant slug. */
export function dockerCommands(i: OciSnippetInput): string {
  const image = `${imageName(i)}:${EXAMPLE_TAG}`;
  return [
    "# Username: the tenant slug. Password: an API key (prk_...) or a CI ID token.",
    `echo "$PREGO_API_KEY" | docker login ${i.ociHost} -u ${i.tenant} --password-stdin`,
    "",
    `docker pull ${image}`,
    "",
    `docker tag ${EXAMPLE_REPO}:${EXAMPLE_TAG} ${image}`,
    `docker push ${image}`,
    "",
  ].join("\n");
}

/** GitLab CI job that logs in with a GitLab ID token, then builds and pushes an image. */
export function gitlabCiDockerYaml(i: OciSnippetInput): string {
  const image = `${imageName(i)}:$CI_COMMIT_SHORT_SHA`;
  return [
    "# .gitlab-ci.yml",
    "build-image:",
    "  image: docker:27",
    "  services: [docker:27-dind]",
    "  id_tokens:",
    "    PREGO_ID_TOKEN:",
    `      aud: ${i.audience}`,
    "  script:",
    `    - echo "$PREGO_ID_TOKEN" | docker login ${i.ociHost} -u ${i.tenant} --password-stdin`,
    `    - docker build -t ${image} .`,
    `    - docker push ${image}`,
    "",
  ].join("\n");
}

/** Kubernetes image pull secret with a read-only API key (ADR-0033 section 4, Q-O4). */
export function kubernetesPullSecret(i: OciSnippetInput): string {
  return [
    "kubectl create secret docker-registry prego-pull \\",
    `  --docker-server=${i.ociHost} \\`,
    `  --docker-username=${i.tenant} \\`,
    '  --docker-password="$PREGO_API_KEY"',
    "",
  ].join("\n");
}
